/**
 * **提取**（`tool.extract`）—— 从抓回来的内容里取出你要的那几个字段。
 *
 * ## 为什么它是**纯节点**（而不是副作用节点）
 *
 * 提取是纯计算：给一段文本 + 一条规则 → 一个结果。所以它
 * **设计期就会跑**（`evaluators`，不是 `runners`）—— 好处很实在：
 * 你在编辑器里改一下规则，**当场**就能看到提取出什么，
 * 而不是"点一次运行才知道对不对"。
 *
 * ⚠️ 串起来就是工具箱那条主线：
 * `事件（手动）→ tool.http（抓）→ tool.extract（取）→ 输出`，
 * 其中只有 `tool.http` 是副作用，其余都能在设计期算。
 *
 * ## 三种模式（刻意的取舍，不是"还没做全"）
 *
 * | 模式 | 干什么 | 为什么是它 |
 * |---|---|---|
 * | `regex` | 正则 + 捕获组 | 万能，而且"抓标题"这种活一行就够 |
 * | `json` | 点路径（`a.b[0].c`） | 抓接口（越来越多） |
 * | `tag` | 取某个标签的**文本**或**属性** | 抓 HTML 最常见的两个动作 |
 *
 * ⚠️ **不做 CSS 选择器引擎**：完整的要引一个 DOM 实现（jsdom / 解析器），
 * 而那是**重依赖 + 环境相关**（求值层要能整块搬进 QuickJS / 服务端，
 * 见 D6）。`tag` 模式覆盖"取 `<title>` 的文本""取 `<meta name=…>` 的
 * content"这类 80% 的活，剩下的交给 `regex`（以及将来的 JS 节点）。
 * 与其假装支持选择器，不如把边界写在台面上。
 */
import type { NodeTypeDef } from '../../node/package'
import type { NodeEvaluator } from '../../node/evaluate'

/** 提取模式 */
export type ExtractMode = 'regex' | 'json' | 'tag'

/** 提取结果（= 这个节点的输出端口值） */
export interface ExtractResult {
  /** 第一个结果（没匹配到就是空串） */
  text: string
  /** 匹配到几个 */
  count: number
  /** 全部结果（**一个端口**，值本身是列表 —— 多路连线 + 列表函数处理） */
  matches: string[]
}

/**
 * 解码常见实体。
 *
 * ⚠️ 必须做：HTML 里的标题十有八九长这样 ——
 * `<title>A &amp; B</title>`，不解码用户拿到的就是 `A &amp; B`，
 * 而他会以为是提取错了。
 *
 * ⚠️ 只解**命名实体有数的那些**：完整实体表是一张长表，
 * 而这里多一个数字引用兜底（`&#39;` / `&#x27;`）已经覆盖绝大多数页面。
 */
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  middot: '·',
  laquo: '«',
  raquo: '»',
  copy: '©',
  reg: '®',
  trade: '™',
  yen: '¥',
  euro: '€',
  pound: '£',
}

export function decodeEntities(input: string): string {
  return input.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (whole, body: string) => {
    if (body.startsWith('#x') || body.startsWith('#X')) {
      const code = Number.parseInt(body.slice(2), 16)
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole
    }
    if (body.startsWith('#')) {
      const code = Number.parseInt(body.slice(1), 10)
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? whole
  })
}

/** 去掉标签、折叠空白（`tag` 模式取文本时用；HTML 里到处是换行和缩进） */
function textOf(html: string): string {
  return decodeEntities(html.replace(/<[^>]*>/g, '')).replace(/\s+/g, ' ').trim()
}

/**
 * 取某个标签的**第一次**出现。
 *
 * `attr` 为空时取标签的文本内容，否则取那个属性的值 ——
 * 这两种正是抓网页时真正要的两件事。
 */
export function extractByTag(
  html: string,
  tag: string,
  attr: string,
): { text: string; matches: string[] } {
  const name = tag.trim()
  if (name === '') return { text: '', matches: [] }
  const wanted = attr.trim().toLowerCase()
  const matches: string[] = []
  /*
   * ⚠️ **取属性和取文本要分开写**（这一条踩过）：
   *   · 取属性时**不能要求闭合标签** —— `<meta name="description" content="…">`
   *     是**空元素**（void element），根本没有 `</meta>`，
   *     按"开标签 + 内容 + 闭标签"去匹配会一个都抓不到（我第一次就是这么写的，
   *     `meta` 的 content 全是空）；
   *   · 而取文本必须要求闭合标签 —— 否则会把整篇文档都当成它的内容。
   */
  const pattern =
    wanted === ''
      ? new RegExp(`<${name}\\b[^>]*>([\\s\\S]*?)</${name}>`, 'gi')
      : new RegExp(`<${name}\\b([^>]*)>`, 'gi')
  for (const match of html.matchAll(pattern)) {
    if (wanted === '') {
      matches.push(textOf(match[1] ?? ''))
      continue
    }
    const attrs = match[1] ?? ''
    const found = new RegExp(`\\b${wanted}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i').exec(attrs)
    if (found) matches.push(decodeEntities(found[2] ?? found[3] ?? found[4] ?? ''))
  }
  return { text: matches[0] ?? '', matches }
}

/** 点路径取值：`a.b[0].c`（`json` 模式） */
export function pickByPath(root: unknown, path: string): unknown {
  const steps = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((step) => step.trim())
    .filter((step) => step !== '')
  let current: unknown = root
  for (const step of steps) {
    if (current === null || current === undefined) return undefined
    if (Array.isArray(current)) {
      const index = Number(step)
      current = Number.isInteger(index) ? current[index] : undefined
      continue
    }
    if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[step]
      continue
    }
    return undefined
  }
  return current
}

/** 把任意值变成"给人看的一段文本"（对象/数组走 JSON） */
function stringify(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/** 跑一次提取（纯函数 —— 单测与求值器共用它） */
export function extract(
  mode: ExtractMode,
  source: string,
  pattern: string,
  flags = '',
  attr = '',
): ExtractResult {
  if (mode === 'tag') {
    const { text, matches } = extractByTag(source, pattern, attr)
    return { text, count: matches.length, matches }
  }
  if (mode === 'json') {
    let parsed: unknown
    try {
      parsed = JSON.parse(source === '' ? 'null' : source)
    } catch (error) {
      throw new Error(`不是合法的 JSON：${error instanceof Error ? error.message : String(error)}`)
    }
    const value = pickByPath(parsed, pattern)
    if (Array.isArray(value)) {
      const matches = value.map((item) => stringify(item))
      return { text: matches[0] ?? '', count: matches.length, matches }
    }
    const text = stringify(value)
    return { text, count: text === '' ? 0 : 1, matches: text === '' ? [] : [text] }
  }

  /* regex */
  if (pattern === '') return { text: '', count: 0, matches: [] }
  let re: RegExp
  try {
    /*
     * ⚠️ 默认**带 `g`**：不带的话 `matchAll` 直接抛，而用户写正则时
     * 想的是"找出所有匹配"，不是"只找第一个"。
     */
    const safe = flags.replace(/[^gimsuy]/g, '')
    re = new RegExp(pattern, safe.includes('g') ? safe : `g${safe}`)
  } catch (error) {
    throw new Error(`正则写错了：${error instanceof Error ? error.message : String(error)}`)
  }
  const matches: string[] = []
  for (const match of source.matchAll(re)) {
    /*
     * 有捕获组就取第 1 组 —— "抓 `<title>(.*?)</title>`" 这种写法里
     * 用户要的显然是括号里的东西，而不是整段匹配。
     */
    const picked = match.length > 1 && match[1] !== undefined ? match[1] : (match[0] ?? '')
    matches.push(decodeEntities(picked))
  }
  return { text: matches[0] ?? '', count: matches.length, matches }
}

export const EXTRACT_TYPE: NodeTypeDef = {
  type: 'tool.extract',
  title: '工具 · 提取内容',
  category: 'tool',
  width: 224,
  keywords: ['extract', 'parse', 'regex', 'json', 'html', '提取', '解析', '正则', '选择'],
  hint: '从抓回来的内容里取出字段（纯计算，编辑时就能看到结果）',
  rows: [
    {
      kind: 'input',
      id: 'source',
      label: '内容',
      socket: { id: 'source', type: 'text' },
      widget: { id: 'source', kind: 'text', value: '' },
    },
    {
      kind: 'property',
      id: 'mode',
      label: '方式',
      widget: {
        id: 'mode',
        kind: 'enum',
        value: 'regex',
        options: [
          { label: '正则', value: 'regex' },
          { label: 'JSON 路径', value: 'json' },
          { label: '标签', value: 'tag' },
        ],
      },
    },
    {
      kind: 'input',
      id: 'pattern',
      label: '规则',
      socket: { id: 'pattern', type: 'text' },
      widget: {
        id: 'pattern',
        kind: 'text',
        value: '<title>([\\s\\S]*?)</title>',
        placeholder: '正则 / 路径（a.b[0]）/ 标签名',
      },
    },
    {
      kind: 'input',
      id: 'attr',
      label: '属性',
      socket: { id: 'attr', type: 'text' },
      widget: { id: 'attr', kind: 'text', value: '', placeholder: '标签模式下取哪个属性（留空 = 取文本）' },
    },
    { kind: 'output', id: 'text', label: '结果', socket: { id: 'text', type: 'text' } },
    { kind: 'output', id: 'count', label: '个数', socket: { id: 'count', type: 'number' } },
    {
      kind: 'output',
      id: 'matches',
      label: '全部',
      /* ★ 一个端口，值是列表 —— 多路连线 + 列表函数处理（用户定的方向） */
      socket: { id: 'matches', type: 'list', itemType: 'text' },
    },
  ],
}

/**
 * 求值器：**设计期就跑**（纯计算，没有副作用）。
 *
 * ⚠️ 输入空（还没抓）时返回空结果而不是报错 —— 设计期上游常常没有值
 * （副作用节点产出占位），那时"什么都没提取到"是**正常状态**，
 * 报错会让每个还没跑的图都挂一个红徽标。
 */
export const extractEvaluator: NodeEvaluator = (api) => {
  const source = String(api.input('source') ?? '')
  const pattern = String(api.input('pattern') ?? '')
  if (source === '') return { text: '', count: 0, matches: [] }
  const mode = String(api.literal('mode') ?? 'regex') as ExtractMode
  const attr = String(api.input('attr') ?? '')
  const result = extract(mode, source, pattern, '', attr)
  return { text: result.text, count: result.count, matches: result.matches }
}
